import type { OnboardCompanyRequest, OnboardedCompany } from "~/api/types";

export interface OnboardInputs {
  orgSlug: string;
  displayName: string;
  // Free text: entries split on commas, spaces or new lines.
  tenantIds: string;
  domains: string;
}

export function parseList(text: string): string[] {
  return [...new Set(text.split(/[\s,]+/).filter(Boolean))];
}

// keycloak-api treats the lists as additions and the org slug as fixed after it is set
// (ADR-0058 decision 9). So the body holds only what is new for the row.
export function buildOnboardBody(
  company: OnboardedCompany | undefined,
  inputs: OnboardInputs,
): OnboardCompanyRequest {
  const tenants = new Set(
    company?.tenants.map((t) => t.tenantId.toLowerCase()) ?? [],
  );
  const domains = new Set(company?.domains ?? []);

  const body: OnboardCompanyRequest = {
    tenantIds: parseList(inputs.tenantIds).filter(
      (id) => !tenants.has(id.toLowerCase()),
    ),
    // keycloak-api stores domains in lowercase.
    domains: parseList(inputs.domains.toLowerCase()).filter(
      (d) => !domains.has(d),
    ),
  };

  const orgSlug = inputs.orgSlug.trim();
  if (!company?.orgSlug && orgSlug) body.orgSlug = orgSlug;

  const displayName = inputs.displayName.trim();
  if (displayName && displayName !== company?.displayName)
    body.displayName = displayName;

  return body;
}

export interface RowState {
  label: string;
  badge: "success" | "warning" | "alert" | "neutral";
  action: string;
}

export function rowState(company: OnboardedCompany): RowState {
  const failedAt = company.failedStep ? `failed at ${company.failedStep}` : "";
  switch (company.status) {
    case "active":
      return {
        label: "Active",
        badge: "success",
        action: "Add tenants or domains",
      };
    case "imported":
      // An imported row keeps its status when a run fails, so its sync does not pause.
      return failedAt
        ? { label: `Incomplete, ${failedAt}`, badge: "alert", action: "Retry" }
        : { label: "Incomplete", badge: "warning", action: "Complete" };
    case "failed":
      return {
        label: `Failed at ${company.failedStep ?? "an unknown step"}`,
        badge: "alert",
        action: "Retry",
      };
    case "onboarding":
      // A run is in progress, or it stopped before it could record the result.
      return { label: "Onboarding", badge: "neutral", action: "Retry" };
  }
}
